package com.hyperether.goodjob.scenes.user

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.material.Icon
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavHostController
import com.hyperether.goodjob.boxCornerShape
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.models.UserStatus
import com.hyperether.goodjob.navigation.UserDetails
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.active
import com.hyperether.goodjob.resources.all_employees
import com.hyperether.goodjob.resources.employees
import com.hyperether.goodjob.resources.ic_active_card
import com.hyperether.goodjob.resources.ic_members_card
import com.hyperether.goodjob.resources.ic_teams_card
import com.hyperether.goodjob.resources.members
import com.hyperether.goodjob.resources.teams
import com.hyperether.goodjob.scenes.components.WebViewHeader
import com.hyperether.goodjob.scenes.user.components.FilterUsersDialog
import com.hyperether.goodjob.scenes.user.components.UserListWeb
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.BorderColor
import com.hyperether.goodjob.theme.DashboardBg
import com.hyperether.goodjob.util.FilterDialogStateManager
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.stringResource
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.lighthousegames.logging.logging

@Composable
fun UsersScreenWeb(
    userViewModel: UserViewModel,
    navController: NavHostController,
    isMobileView: Boolean,
    screenHeight: Float = 0f
) {

    val statsHeight = 124.toScaleDp().value
    val webHeader = 136.toScaleDp().value
    val paddingHeight = 64.toScaleDp().value
    val tabHeight = 64.toScaleDp().value
    val paginationHeight = 70.toScaleDp().value
    val listHeight = screenHeight - statsHeight - webHeader - paddingHeight - tabHeight - paginationHeight

    val log = logging("UsersScreenWeb")
    val itemsPerPage =
        remember { mutableStateOf((listHeight.toInt() / 62).toInt() - 1) } // Start with default, will be updated
    val currentPage = remember { mutableStateOf(1) }

    var searchText = remember { mutableStateOf("") }
    var searchTextTeam = remember { mutableStateOf("") }
    val sortBy = remember { mutableStateOf<String?>(null) }
    val showFilterDialog = remember { mutableStateOf(false) }

    val userList by userViewModel.users.collectAsStateWithLifecycle()
    val usersFiltered by userViewModel.filteredUsers.collectAsStateWithLifecycle()
    val teams by userViewModel.teams.collectAsStateWithLifecycle()

    val selectedTeams = remember { mutableStateOf(mapOf<String, Boolean>()) }
    val filteredUsersSearch = if (searchText.value.isNotBlank()) {
        usersFiltered.filter { user ->
            user.getName().contains(searchText.value, ignoreCase = true)
        }
    } else {
        usersFiltered
    }
    val sortedList = filteredUsersSearch.sortedBy { user ->
        (user.fullName ?: user.email)?.lowercase()
    }

    val currentUsers = derivedStateOf {
        sortedList.drop((currentPage.value - 1) * itemsPerPage.value)
            .take(itemsPerPage.value)
    }
    log.d("UsersScreenWeb") {
        "recompose: currentPage: ${currentPage.value} " +
                "currentUsers: ${currentUsers.value.size} sortedList: ${sortedList.size} " +
                "usersFiltered: ${usersFiltered.size} calculatedItemsPerPage: $itemsPerPage " +
                "screenHeight: $screenHeight listHeight: $listHeight"
    }

    LaunchedEffect(Unit) {
        userViewModel.init()
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(DashboardBg)
    ) {
        WebViewHeader(
            isAddNewUser = true,
            isBackAvailable = false,
            title = stringResource(Res.string.employees),
            isImportAndAddVisible = !userViewModel.isEmployee(),
            navHostController = navController
        )
        Spacer(modifier = Modifier.height(15.toScaleDp()))
        StatsSection(
            teams = teams.size,
            users = userViewModel.users.value
        )

        Spacer(modifier = Modifier.height(13.toScaleDp()))

        // User List
        DisableSelection {
            UserListWeb(
                viewModel = userViewModel,
                isEmployeeList = true,
                title = stringResource(Res.string.all_employees),
                allItems = sortedList,
                filteredItems = currentUsers.value,
                sortBy = sortBy,
                searchText = searchText,
                searchTextTeam = searchTextTeam,
                currentPage = currentPage,
                itemsPerPage = itemsPerPage.value,
                showFilterDialog = showFilterDialog,
                deleteItemById = { id ->
                    userViewModel.deleteUser(id, {
                        if (userList.size < (itemsPerPage.value * currentPage.value) && currentPage.value > 1) {
                            log.d("UsersScreen") { "User deleted. currentPage: ${currentPage.value} userList.size:${userList.size}" }
                            if (userList.size <= itemsPerPage.value * (currentPage.value - 1)) {
                                currentPage.value = currentPage.value - 1
                                log.d("UsersScreen") { "currentPage set to: ${currentPage.value}" }
                            }
                        }
                    })
                },
                saveSelectedItem = { id ->
                    log.d("UsersScreen") { "SELECTED USER: $id" }
                    navController.navigate(UserDetails(id))
                },
                teams = teams,
                selectedTeams = selectedTeams,
                onTeamCreated = { team ->
                    userViewModel.createTeam(team)
                    searchTextTeam.value = ""
                },
                onCheckedChange = { id, isChecked ->
                    val updatedSelection = selectedTeams.value.toMutableMap()
                    updatedSelection[id] = isChecked
                    selectedTeams.value = updatedSelection
                },
                onDeleteTeamClick = {
                    userViewModel.showLoader.value = true
                    val selectedTeamIds = selectedTeams.value.filterValues { it }.keys
                    val selectedTeamList =
                        teams.filter { it._id in selectedTeamIds }
                    userViewModel.deleteSelectedTeams(
                        selectedTeamList,
                        onAllDeleted = {
                            if (teams.size < (itemsPerPage.value * currentPage.value) && currentPage.value > 1) {
                                log.d("UsersScreen") { "Team deleted. currentPage: ${currentPage.value} teams.size:${teams.size}" }
                                if (teams.size <= itemsPerPage.value * (currentPage.value - 1)) {
                                    currentPage.value = currentPage.value - 1
                                    log.d("UsersScreen") { "currentPage set to: ${currentPage.value}" }
                                }
                            }
                        },
                        onError = {
                        }
                    )
                },
                onCalculatedItemsPerPageChanged = { newItemsPerPage ->
//                if (newItemsPerPage > 0 && newItemsPerPage != calculatedItemsPerPage.value) {
//                    calculatedItemsPerPage.value = newItemsPerPage
//                    log.d("UsersScreen") { "calculatedItemsPerPage updated to: ${calculatedItemsPerPage.value}" }
//                }
                }
            )
        }
    }

    if (showFilterDialog.value) {
        FilterUsersDialog(
            cityList = userList.mapNotNull { it.city?.trim() }.filter { it.isNotBlank() }
                .distinct(),
            roleList = userList.mapNotNull {
                val role =
                    userViewModel.userRoles.entries.find { entry -> entry.key == it.role }?.value
                role?.let {
                    stringResource(it)
                }
            }.distinct(),
            teamList = userList.mapNotNull { it.teams }.flatMap { teamList ->
                teamList.mapNotNull { team ->
                    team?.value?.takeIf { it.isNotBlank() }
                }
            }.distinct(),
            statusList = userList.mapNotNull { user ->
                if (user.confirmStatus.equals(UserStatus.pending.name, ignoreCase = true)) {
                    val confirmStatus = userViewModel.userStatues.entries.find { entry ->
                        entry.key == user.confirmStatus
                    }?.value

                    confirmStatus?.let {
                        stringResource(it)
                    }
                } else {
                    val status = userViewModel.userStatues.entries.find { entry ->
                        entry.key == user.status
                    }?.value
                    status?.let {
                        stringResource(it)
                    }
                }
            }.distinct(),
            skillList = userList.flatMap { it.skills ?: emptyList() }.distinct(),
            userViewModel,
            isMobileView = isMobileView,
            onDismiss = {
                showFilterDialog.value = false
            },
            onApply = { selectedCities, selectedRoles, selectedTeams, selectedStatuses, selectedSkills ->
                FilterDialogStateManager.setDialogShowing(false)
                userViewModel.onApplyFilter(
                    cities = selectedCities,
                    roles = selectedRoles,
                    teams = selectedTeams,
                    statuses = selectedStatuses,
                    skills = selectedSkills
                )
            }
        )
    }
}

@Composable
fun StatsSection(teams: Int, users: List<User>) {
    val activeUsers = users.filter { it.status == UserStatus.active.name }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(124.toScaleDp())
            .padding(horizontal = 75.toScaleDp())
            .background(Color.White)
            .border(
                width = 1.toScaleDp(),
                color = BorderColor,
                shape = boxCornerShape()
            ),
        horizontalArrangement = Arrangement.SpaceEvenly
    ) {
        Box(modifier = Modifier.weight(1f)) {
            StatCard(
                title = stringResource(Res.string.teams),
                value = teams.toString(),
                change = "",
                resource = Res.drawable.ic_teams_card
            )
        }
        VerticalDivider()
        Box(modifier = Modifier.weight(1f)) {
            StatCard(
                title = stringResource(Res.string.members),
                value = users.size.toString(),
                change = "",
                resource = Res.drawable.ic_members_card
            )
        }
        VerticalDivider()
        Box(modifier = Modifier.weight(1f)) {
            StatCard(
                title = stringResource(Res.string.active),
                value = activeUsers.size.toString(),
                change = "",
                resource = Res.drawable.ic_active_card
            )
        }
    }
}

@Composable
fun StatCard(title: String, value: String, change: String, resource: DrawableResource) {
    Column(
        modifier = Modifier
            .background(Color.White, shape = RoundedCornerShape(8.toScaleDp()))
            .padding(horizontal = 56.toScaleDp()),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Row(
            modifier = Modifier.padding(vertical = 16.toScaleDp())
        ) {
            Icon(
                modifier = Modifier
                    .height(69.toScaleDp())
                    .width(69.toScaleDp()),
                painter = painterResource(resource),
                contentDescription = null,
                tint = BlueApp
            )
            Spacer(modifier = Modifier.width(32.toScaleDp()))
            Column {
                Text(
                    text = title,
                    fontSize = 16.toScaleSp(),
                    color = Color.Gray,
                    fontWeight = FontWeight.W400,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Spacer(modifier = Modifier.height(8.toScaleDp()))
                Text(
                    text = value,
                    fontSize = 32.toScaleSp(),
                    fontWeight = FontWeight.W700,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                // TODO: add CHANGE when api is ready
//                if (change.isNotEmpty()) {
//                    Text(
//                        text = change,
//                        fontSize = Sp(16f),
//                        color = if (change.startsWith("+")) Color.Green else Color.Red
//                    )
//                }
            }
        }
    }
}

@Composable
fun VerticalDivider() {
    Box(
        modifier = Modifier
            .fillMaxHeight(0.8f)
            .width(1.toScaleDp())
            .padding(top = 20.toScaleDp())
            .background(BorderColor)
    )
}
